import React, { useCallback, useEffect, useState, useContext } from 'react'
import { CHAT, createGenesysConversation, MESSAGE } from 'api/supportAssistant'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { SocketContext } from '../../contexts/SocketContext'

import useSocketEvent from '../../hooks/useSocketEvent'
import TextButton from 'LuxKit/Button/TextButton'
import { getActionIcon } from 'supportAssistant/functions/getActionIcon'
import Group from 'components/utils/Group'
import { selectCurrentChat, selectOutageMessage } from 'supportAssistant/selectors/selectCurrentChat'
import { connect } from 'react-redux'
import { setChatOutageMessage, setCurrentChatId } from 'actions/supportAssistant/supportAssistantActions'

interface MappedStateProps {
  currentChat?: any;
  outageMessage: string | undefined;
}

interface Props {
  loadingMessages?: boolean;
}

export function Agent(props: Props & MappedStateProps) {
  const {
    currentChat,
    outageMessage,
    loadingMessages,
  } = props
  const account = useAppSelector(state => state.auth.account)

  const dispatch = useAppDispatch()

  const { onLogin, socket } = useContext(SocketContext)

  const userIsLoggedIn = useAppSelector(selectLoggedIn)

  const [supportAgentSuggestion, setSupportAgentSuggestion] = useState<boolean>(false)
  const [agentRequestedChatId, setAgentRequestedChatId] = useState<string | null>(null)

  useEffect(() => {
    if (outageMessage) {
      setSupportAgentSuggestion(true)
      setAgentRequestedChatId(currentChat?.id)
    }
  }, [outageMessage, currentChat?.id])

  const onChatPromptSupport = useCallback(({ hasDeadEnd, hasAgentRequest, chatId }: { hasDeadEnd: boolean, hasAgentRequest: boolean, chatId?: string }) => {
    if (hasDeadEnd || hasAgentRequest) {
      setSupportAgentSuggestion(true)
      if (chatId) {
        setAgentRequestedChatId(chatId)
        dispatch(setCurrentChatId(chatId))
      }
    }
  }, [dispatch])

  useSocketEvent(CHAT.PROMPT_SUPPORT, onChatPromptSupport)

  // dirty fix, needs to be removed when we refactor agent pills to be same as LESA v2 flow
  socket?.on(MESSAGE.RESPONSE, () => {
    setSupportAgentSuggestion(false)
    setAgentRequestedChatId(null)
  })

  const onConnectToSupport = useCallback(async(customer: App.AuthAccount) => {
    setSupportAgentSuggestion(false)
    setAgentRequestedChatId(null)

    try {
      await createGenesysConversation({
        chatId: currentChat?.id,
        customerId: customer.memberId!,
        customer: {
          firstName: customer.givenName,
          lastName: customer.surname,
          email: customer.email,
          phone: customer.phone,
        },
      })
      dispatch(setChatOutageMessage(undefined))
    } catch (error) {
      console.warn(error)
    }
  }, [currentChat?.id, dispatch])

  const onRejectSupport = useCallback(() => {
    setSupportAgentSuggestion(false)
    setAgentRequestedChatId(null)
  }, [])

  const onLoginClick = useCallback(() => {
    onLogin().then(({ account }) => {
      if (currentChat?.id && account) {
        onConnectToSupport(account)
      }
    })
  }, [onLogin, onConnectToSupport, currentChat?.id])

  const showSupportAgentSuggestion = !loadingMessages && supportAgentSuggestion && agentRequestedChatId == currentChat?.id

  const ChatAgentIcon = getActionIcon('chatRecent')
  const CancelIcon = getActionIcon('cancel')

  return (
    <Group direction="vertical">
      {showSupportAgentSuggestion && userIsLoggedIn && <>
        <BodyTextBlock variant="small">
          Connect to a support agent?
        </BodyTextBlock>
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          <TextButton
            kind="secondary"
            variant="dark"
            onClick={() => onConnectToSupport(account)}
            startIcon={ChatAgentIcon ? <ChatAgentIcon /> : undefined}
          >
            Yes, please connect me to an agent
          </TextButton>
          {!outageMessage && <TextButton
            kind="secondary"
            variant="dark"
            onClick={onRejectSupport}
            startIcon={CancelIcon ? <CancelIcon /> : undefined}
          >
            No thank you, I want to keep speaking to you!
          </TextButton>}
        </Group>
      </>}
      {showSupportAgentSuggestion && !userIsLoggedIn && <>
        <BodyTextBlock variant="small">
          Log in to connect to Support Agent
        </BodyTextBlock>
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          <TextButton
            kind="secondary"
            variant="dark"
            onClick={onLoginClick}
            startIcon={ChatAgentIcon ? <ChatAgentIcon /> : undefined}
          >
            Log in and connect to agent
          </TextButton>
          <TextButton
            kind="secondary"
            variant="dark"
            onClick={onRejectSupport}
            startIcon={CancelIcon ? <CancelIcon /> : undefined}
          >
            No thank you
          </TextButton>
        </Group>
      </>}
    </Group>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((state) => {
  return {
    currentChat: selectCurrentChat(state),
    outageMessage: selectOutageMessage(state),
  }
})(Agent)
